import React from 'react';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import Fab from '@material-ui/core/Fab';
import TextField from '@material-ui/core/TextField';
import AddIcon from '@material-ui/icons/Add';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Add } from '@material-ui/icons';
// Components
import App from '../../../../components/app';
import { Dialog } from '../../../../components/dialog';
import ErrorMessage from '../../../../components/error-message';
import MyLink from '../../../../components/mylink/mylink';
import { TooltipButton } from '../../../../components/tooltip-button';
import ButtonTo from '../../../../components/buttonTo';
import CustomizedTreeView from '../../../../components/customTree';
// Modules
import { Price, RootPricing } from '../../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import { actions as pricingActions, selectors as pricingSelectors } from '../../../../redux/reducers/pricing';
// Ressources
import withGlobalStyles from '../../../../utils/global-styles';
import StringUtils from '../../../../utils/stringUtils';
import UserUtils, { AccountRight, RightRank } from '../../../../utils/userUtils';
import { paths } from '../../../paths';
import { clsx } from '../../../../utils/htmlUtils';

interface PricingPricesListProps extends WithTranslation {
    globalClasses: any;
    prices: Price[];
    pricesTree: RootPricing;
    getPricesListError: ErrorPayload;
    getPricingSets: () => void;
    getPricingSetsTree: () => void;
    deletePricingSet: (id: string) => void;
}

interface PricingPricesListState {
    filterText: string;
}

const MANAGE_PRICES = paths.pricingManagePrices;

class PricingPricesListPage extends React.Component<PricingPricesListProps, PricingPricesListState> {
    isAdmin: boolean;

    constructor(props) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PRICING_MGMT, mode: RightRank.W });
        this.state = {
            filterText: '',
        };
    }

    componentDidMount() {
        this.props.getPricingSetsTree();
    }

    handleDelete = (obj: Price) => (e) => {
        if (obj.id) {
            this.props.deletePricingSet(obj.id);
        } else {
            console.error("Can't delete", obj);
        }
    };

    //=======================================//
    //=============== Renders ===============//
    //=======================================//

    renderRow = (price: Price): JSX.Element => {
        const { t, globalClasses } = this.props;
        const { id, name } = price;
        return (
            <div key={id} style={{ display: 'flex', flex: 1, alignItems: 'center', marginTop: 18, marginBottom: 18 }}>
                <MyLink style={{ flex: 3 }} color="secondary" to={`${MANAGE_PRICES}/${price.id}`}>
                    {name}
                </MyLink>
                {this.isAdmin && (
                    <Dialog
                        btnTitle={t('common:validate')}
                        description={t('common:deleteParam', {
                            id: `(${price.id}) ${price.name}`,
                        })}
                        customButton={
                            <TooltipButton title={t('common:delete')}>
                                <Button
                                    size="small"
                                    style={{ marginLeft: 8 }}
                                    variant="contained"
                                    color="primary"
                                    className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                                >
                                    <Icon fontSize="small">delete</Icon>
                                </Button>
                            </TooltipButton>
                        }
                        onOK={this.handleDelete(price)}
                    />
                )}
            </div>
        );
    };

    // Keep a node when its name matches the filter or any of its descendants match,
    // so nested matches stay reachable in the tree.
    filterTree = (nodes: any[], needle: string): any[] => {
        return nodes.reduce((acc, node) => {
            const children = node.children ? this.filterTree(node.children, needle) : [];
            const selfMatches = StringUtils.normalizeForSearch(node.name).includes(needle);
            if (selfMatches || children.length > 0) {
                acc.push({ ...node, children });
            }
            return acc;
        }, []);
    };

    // Ids of nodes that still have children after filtering, so their branch is expanded.
    collectExpandedIds = (nodes: any[]): string[] => {
        return nodes.reduce((acc, node) => {
            if (node.children?.length > 0) {
                acc.push(node.id, ...this.collectExpandedIds(node.children));
            }
            return acc;
        }, []);
    };

    renderAreaTree = (): JSX.Element => {
        const { pricesTree } = this.props;
        const { filterText } = this.state;
        if (!pricesTree?.children) {
            return null;
        }
        const needle = StringUtils.normalizeForSearch(filterText.trim());
        const datas = needle ? this.filterTree(pricesTree.children, needle) : pricesTree.children;
        const expandedIds = needle ? this.collectExpandedIds(datas) : undefined;
        return <CustomizedTreeView datas={datas} expandedIds={expandedIds} customRow={this.renderRow} />;
    };

    renderFAB = () => {
        const { t, globalClasses } = this.props;
        return (
            this.isAdmin && (
                <>
                    <br />
                    <br />
                    <Fab
                        className={globalClasses.fabBotRight}
                        size="large"
                        component={RouterLink}
                        to={`${MANAGE_PRICES}/add`}
                        color="secondary"
                        aria-label={t('common:add')}
                    >
                        <AddIcon />
                    </Fab>
                </>
            )
        );
    };

    render() {
        const { t, globalClasses, pricesTree, getPricesListError } = this.props;
        const { filterText } = this.state;

        const payload = pricesTree || {};

        return (
            <App
                needRights={AccountRight.PRICING_MGMT}
                loading={!(payload && payload.children)}
                header={{
                    title: t('sets.manage_sets'),
                    button: (
                        <ButtonTo
                            rights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.W }}
                            startIcon={<Add />}
                            to={`${MANAGE_PRICES}/add`}
                            text={t('common:add')}
                        />
                    ),
                }}
            >
                <ErrorMessage payload={getPricesListError} />
                {payload?.children && (
                    <>
                        {payload.children.length === 0 && (
                            <p>
                                <b>{t('sets.no_pricings')}</b>
                            </p>
                        )}
                        {payload.children.length > 0 && (
                            <div className={globalClasses.block} style={{ flex: 1 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    label={t('sets.filter_sets')}
                                    value={filterText}
                                    onChange={(event) => this.setState({ filterText: event.target.value })}
                                />
                                {this.renderAreaTree()}
                            </div>
                        )}
                    </>
                )}
                {/* {this.renderFAB()} */}
            </App>
        );
    }
}

const PricingPricesListPageWithTranslation = withGlobalStyles(
    withTranslation(['pricing', 'common'])(PricingPricesListPage),
);

const mapStateToProps = (state) => ({
    prices: pricingSelectors.prices(state),
    pricesTree: pricingSelectors.pricesTree(state),
    getPricesListError: errorSelectors.getPricesListError(state),
});

const mapDispatchToProps = {
    getPricingSets: pricingActions.getPricingSets,
    getPricingSetsTree: pricingActions.getPricingSetsTree,
    deletePricingSet: pricingActions.deletePricingSet,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingPricesListPageWithTranslation);
